/**
 * Moderation API Client
 * Handles API calls for content moderation operations
 * Iteration 5 - Moderation System
 */

import apiClient from './client';
import { API_ENDPOINTS } from '../config/api';
import type { ModeratePostRequest, RestoreContentRequest, EditHistoryResponse } from '../types/moderation';
import type { Post } from '../types/post';
import type { CommentPaginatedResponse } from '../types/comment';
import type { PaginatedResponse } from '../types/post';

/**
 * Soft delete a post (moderators only)
 */
const deletePost = async (postId: string, request: ModeratePostRequest): Promise<void> => {
  await apiClient.post(`/moderation/posts/${postId}/delete`, request);
};

/**
 * Restore a soft-deleted post (moderators only)
 */
const restorePost = async (postId: string, request: RestoreContentRequest): Promise<Post> => {
  const response = await apiClient.post(`/moderation/posts/${postId}/restore`, request);
  return response.data;
};

/**
 * Get edit history for a post
 */
const getPostEditHistory = async (postId: string): Promise<EditHistoryResponse[]> => {
  const response = await apiClient.get(`/moderation/posts/${postId}/edit-history`);
  return response.data;
};

/**
 * Permanently delete a soft-deleted post (moderators only)
 */
const permanentlyDeletePost = async (postId: string): Promise<void> => {
  await apiClient.delete(`/moderation/posts/${postId}/permanent`);
};

/**
 * Get all deleted comments with pagination (moderators only)
 */
const getDeletedComments = async (page: number = 0, size: number = 50): Promise<CommentPaginatedResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('size', size.toString());
  const response = await apiClient.get<CommentPaginatedResponse>(`/moderation/comments/deleted?${params.toString()}`);
  return response.data;
};

/**
 * Soft delete a comment (moderators only)
 */
const deleteComment = async (commentId: string, request: ModeratePostRequest): Promise<void> => {
  await apiClient.post(`/moderation/comments/${commentId}/delete`, request);
};

/**
 * Restore a soft-deleted comment (moderators only)
 */
const restoreComment = async (commentId: string, request: RestoreContentRequest): Promise<void> => {
  await apiClient.post(`/moderation/comments/${commentId}/restore`, request);
};

/**
 * Permanently delete a soft-deleted comment (moderators only)
 */
const permanentlyDeleteComment = async (commentId: string): Promise<void> => {
  await apiClient.delete(`/moderation/comments/${commentId}/permanent`);
};

/**
 * Get flagged posts (pending flag) – MODERATOR only
 */
const getFlaggedPosts = async (page: number = 0, size: number = 50): Promise<PaginatedResponse<Post>> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('size', size.toString());
  const response = await apiClient.get<PaginatedResponse<Post>>(`${API_ENDPOINTS.MODERATION_FLAGGED_POSTS}?${params.toString()}`);
  return response.data;
};

/**
 * Approve flag (uphold). Post remains hidden. MODERATOR only.
 */
const approveFlag = async (postId: string, request?: { reason?: string }): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.MODERATION_APPROVE_FLAG(postId), request ?? {});
};

/**
 * Reject flag (dismiss). Post becomes visible again. MODERATOR only.
 */
const rejectFlag = async (postId: string, request?: { reason?: string }): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.MODERATION_REJECT_FLAG(postId), request ?? {});
};

/**
 * Get flagged comments (pending flag) – MODERATOR only
 */
const getFlaggedComments = async (page: number = 0, size: number = 50): Promise<CommentPaginatedResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('size', size.toString());
  const response = await apiClient.get<CommentPaginatedResponse>(`${API_ENDPOINTS.MODERATION_FLAGGED_COMMENTS}?${params.toString()}`);
  return response.data;
};

/**
 * Approve comment flag (uphold). Comment is soft-deleted. MODERATOR only.
 */
const approveCommentFlag = async (commentId: string, request?: { reason?: string }): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.MODERATION_APPROVE_COMMENT_FLAG(commentId), request ?? {});
};

/**
 * Reject comment flag (dismiss). Comment becomes visible again. MODERATOR only.
 */
const rejectCommentFlag = async (commentId: string, request?: { reason?: string }): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.MODERATION_REJECT_COMMENT_FLAG(commentId), request ?? {});
};

/**
 * Moderation API object
 */
export const moderationApi = {
  deletePost,
  restorePost,
  getPostEditHistory,
  permanentlyDeletePost,
  getDeletedComments,
  deleteComment,
  restoreComment,
  permanentlyDeleteComment,
  getFlaggedPosts,
  approveFlag,
  rejectFlag,
  getFlaggedComments,
  approveCommentFlag,
  rejectCommentFlag,
};

// Named exports for convenience
export { deletePost, restorePost, getPostEditHistory, permanentlyDeletePost, getDeletedComments, deleteComment, restoreComment, permanentlyDeleteComment, getFlaggedPosts, approveFlag, rejectFlag, getFlaggedComments, approveCommentFlag, rejectCommentFlag };
